import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { applicationService, complianceService, notificationService, settlementService } from '../services/api';
import DashboardLayout from '../components/DashboardLayout';
import ComplianceChecklist from '../components/ComplianceChecklist';
import ComplianceStatusCard from '../components/ComplianceStatusCard';
import { Route, FileSignature, Truck, ClipboardList, CheckCircle2, DollarSign, FileText, Ban, CreditCard } from 'lucide-react';

const ApplicantDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [application, setApplication] = useState(null);
  const [complianceStatus, setComplianceStatus] = useState(null);
  const [settlements, setSettlements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchData();
  }, [user]);

  const fetchData = async () => {
    try {
      // Try to get existing application
      try {
        const appData = await applicationService.getMyApplication();
        setApplication(appData);
      } catch (error) {
        setApplication(null);
      }

      // Get compliance status
      try {
        const compStatus = await complianceService.getStatus();
        setComplianceStatus(compStatus);
      } catch (error) {
        console.error('Failed to fetch compliance status:', error);
      }

      // Get settlements for active drivers
      if (user?.workflow_state === 'active' || user?.workflow_state === 'suspended') {
        try {
          const settlementsData = await settlementService.getDriverSettlements(user.id);
          setSettlements(Array.isArray(settlementsData) ? settlementsData : []);
        } catch (error) {
          console.error('Failed to fetch settlements:', error);
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const showComplianceSection = [
    'conditional_approval',
    'compliance_incomplete',
    'compliance_under_review',
    'active',
    'suspended',
    'expired_docs'
  ].includes(user?.workflow_state);

  const showSettlements = ['active', 'suspended', 'expired_docs'].includes(user?.workflow_state);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center items-center h-64">
          <div className="flex flex-col items-center gap-3">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
            <p className="text-gray-500">Loading your dashboard...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-4 sm:space-y-6">
        {/* Welcome Header - Mobile Optimized */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">
              Welcome back, {user?.first_name || 'Driver'}
            </h1>
            <p className="text-sm sm:text-base text-gray-600 mt-1">
              {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
            </p>
          </div>
          <ComplianceStatusCard status={user?.workflow_state} compact />
        </div>

        {/* Status Banner - Enhanced for Mobile */}
        <ComplianceStatusCard status={user?.workflow_state} />

        {/* Tab Navigation - Mobile First */}
        {(showComplianceSection || showSettlements) && (
          <div className="border-b border-gray-200 overflow-x-auto">
            <nav className="flex -mb-px min-w-max">
              <button
                onClick={() => setActiveTab('overview')}
                className={`py-3 px-4 border-b-2 font-medium text-sm whitespace-nowrap ${
                  activeTab === 'overview'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Overview
              </button>
              {showComplianceSection && (
                <button
                  onClick={() => setActiveTab('compliance')}
                  className={`py-3 px-4 border-b-2 font-medium text-sm whitespace-nowrap ${
                    activeTab === 'compliance'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Compliance
                </button>
              )}
              {showSettlements && (
                <button
                  onClick={() => setActiveTab('settlements')}
                  className={`py-3 px-4 border-b-2 font-medium text-sm whitespace-nowrap ${
                    activeTab === 'settlements'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Settlements
                </button>
              )}
            </nav>
          </div>
        )}

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-4 sm:space-y-6">
            {/* No Application - Create One */}
            {!application && user?.workflow_state === 'account_created' && (
              <div className="bg-white shadow-md rounded-lg p-4 sm:p-6">
                <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">Get Started</h2>
                <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">
                  Start your application to join BlueLock Freights. Choose your program track below.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                  <button
                    onClick={() => navigate('/application/new?type=driver&path=dispatch_only')}
                    className="p-4 sm:p-6 border-2 border-blue-600 rounded-lg hover:bg-blue-50 text-left transition-colors active:bg-blue-100"
                    data-testid="dispatch-only-btn"
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <Route className="w-7 h-7 text-blue-600" />
                      <h3 className="font-bold text-base sm:text-lg">Dispatch-Only</h3>
                    </div>
                    <p className="text-xs sm:text-sm text-gray-600">I operate under my own DOT/MC authority. (8% gross or $400/week per truck)</p>
                  </button>
                  <button
                    onClick={() => navigate('/application/new?type=driver&path=lease_on')}
                    className="p-4 sm:p-6 border-2 border-blue-600 rounded-lg hover:bg-blue-50 text-left transition-colors active:bg-blue-100"
                    data-testid="lease-on-btn"
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <FileSignature className="w-7 h-7 text-blue-600" />
                      <h3 className="font-bold text-base sm:text-lg">Lease-On</h3>
                    </div>
                    <p className="text-xs sm:text-sm text-gray-600">I will operate under company authority. (84% / 16% split on linehaul)</p>
                  </button>
                  <button
                    onClick={() => navigate('/application/new?type=driver&path=company_driver')}
                    className="p-4 sm:p-6 border-2 border-blue-600 rounded-lg hover:bg-blue-50 text-left transition-colors active:bg-blue-100"
                    data-testid="company-driver-btn"
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <Truck className="w-7 h-7 text-blue-600" />
                      <h3 className="font-bold text-base sm:text-lg">Company Driver</h3>
                    </div>
                    <p className="text-xs sm:text-sm text-gray-600">I will drive a BlueLock-provided truck under company authority.</p>
                  </button>
                </div>
              </div>
            )}

            {/* Application In Progress */}
            {application && (user?.workflow_state === 'application_started' || user?.workflow_state === 'application_submitted') && (
              <div className="bg-white shadow-md rounded-lg p-4 sm:p-6">
                <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">Your Application</h2>
                <div className="mb-4">
                  <div className="flex items-center gap-2 text-gray-600">
                    <ClipboardList className="w-5 h-5" />
                    <span className="text-sm sm:text-base">
                      <strong>Authority Path:</strong> {application.authority_path?.replace(/_/g, ' ').toUpperCase()}
                    </span>
                  </div>
                </div>
                {user?.workflow_state === 'application_started' && (
                  <button
                    onClick={() => navigate('/application/edit')}
                    className="w-full sm:w-auto bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium active:bg-blue-800"
                    data-testid="continue-application-btn"
                  >
                    Continue Application
                  </button>
                )}
              </div>
            )}

            {/* Quick Stats for Active Drivers */}
            {user?.workflow_state === 'active' && (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <CheckCircle2 className="w-6 h-6 mb-1 text-green-600" />
                  <p className="text-xs text-gray-500">Status</p>
                  <p className="font-semibold text-green-600">Active</p>
                </div>
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <DollarSign className="w-6 h-6 mb-1 text-green-600" />
                  <p className="text-xs text-gray-500">Settlements</p>
                  <p className="font-semibold text-gray-900">{settlements.length}</p>
                </div>
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <FileText className="w-6 h-6 mb-1 text-blue-600" />
                  <p className="text-xs text-gray-500">Compliance</p>
                  <p className="font-semibold text-gray-900">
                    {complianceStatus?.completed_items || 0}/{complianceStatus?.total_items || 0}
                  </p>
                </div>
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <FileSignature className="w-6 h-6 mb-1 text-blue-600" />
                  <p className="text-xs text-gray-500">Authority</p>
                  <p className="font-semibold text-gray-900 capitalize">
                    {application?.authority_path?.replace('_', ' ') || 'N/A'}
                  </p>
                </div>
              </div>
            )}

            {/* Suspended Message */}
            {user?.workflow_state === 'suspended' && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 sm:p-6" data-testid="suspended-message">
                <div className="flex items-start gap-3">
                  <Ban className="w-6 h-6 text-red-600 flex-shrink-0" />
                  <div>
                    <h3 className="text-base sm:text-lg font-bold text-red-900 mb-2">
                      Account Suspended
                    </h3>
                    <p className="text-sm sm:text-base text-red-800">
                      Your status has been suspended. Please review your compliance checklist and contact support.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Compliance Tab */}
        {activeTab === 'compliance' && showComplianceSection && (
          <ComplianceChecklist />
        )}

        {/* Settlements Tab */}
        {activeTab === 'settlements' && showSettlements && (
          <div className="bg-white shadow-md rounded-lg overflow-hidden">
            <div className="p-4 sm:p-6 border-b border-gray-200">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900">My Settlements</h2>
              <p className="text-sm text-gray-600 mt-1">View your payment history and settlement details.</p>
            </div>
            
            {settlements.length === 0 ? (
              <div className="p-8 text-center">
                <CreditCard className="w-10 h-10 mb-3 text-gray-400 mx-auto" />
                <p className="text-gray-500">No settlements yet</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {settlements.slice(0, 10).map((settlement) => (
                  <div key={settlement.id} className="p-4 hover:bg-gray-50">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <div>
                        <p className="font-medium text-gray-900">
                          {new Date(settlement.period_start).toLocaleDateString()} - {new Date(settlement.period_end).toLocaleDateString()}
                        </p>
                        <p className="text-sm text-gray-500 capitalize">
                          Status: {settlement.status}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-green-600">
                          ${settlement.net_pay?.toFixed(2) || '0.00'}
                        </p>
                        <p className="text-xs text-gray-500">
                          Gross: ${settlement.gross_pay?.toFixed(2) || '0.00'}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ApplicantDashboard;
